import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LocalStorageService } from '../local-storage/local-storage.service';

const SESSION_KEY = 'operatorSessionId';
const SESSION_STARTED_KEY = 'operatorSessionStartedAt';
const PING_INTERVAL_MS = 30_000;
const INACTIVITY_KEY = 'lastActivityAt';

@Injectable({ providedIn: 'root' })
export class OperatorSessionService implements OnDestroy {
  private readonly apiURL = `${environment.apiUrl}/v${environment.apiVersion}/operator-session`;
  private pingTimer: any;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
  ) {}

  get sessionId(): number | null {
    return this.localStorageService.get(SESSION_KEY) ?? null;
  }

  /** Inicio da sessao atual (ISO). O live status do supervisor nao serve aqui:
   *  a query dele exclui de proposito as contas com perfil de supervisao. */
  get sessionStartedAt(): string | null {
    return this.localStorageService.get(SESSION_STARTED_KEY) ?? null;
  }

  async createSession(clinicId: number, username: string, machine: string, ticketWindowId: number): Promise<number> {
    const response = await firstValueFrom(
      this.http.post<any>(this.apiURL, { clinicId, username, machine, ticketWindowId })
    );
    const id = response?.data?.[0];
    if (id) {
      this.localStorageService.set(SESSION_KEY, id);
      this.localStorageService.set(SESSION_STARTED_KEY, new Date().toISOString());
      this.startPing();
    }
    return id;
  }

  async endSession(motivo?: string): Promise<void> {
    const id = this.sessionId;
    if (!id) return;
    this.stopPing();
    try {
      await firstValueFrom(this.http.delete(`${this.apiURL}/${id}${motivo ? '?motivo=' + motivo : ''}`));
    } catch { /* silencioso — aba pode estar fechando */ }
    this.localStorageService.remove(SESSION_KEY);
    this.localStorageService.remove(SESSION_STARTED_KEY);
  }

  /** Navegação interna: só registra o evento na trilha — sessão e ping continuam */
  notifyNavigation(): void {
    const id = this.sessionId;
    if (!id) return;
    firstValueFrom(this.http.put(`${this.apiURL}/${id}/navigate`, {})).catch(() => {});
  }

  private startPing(): void {
    this.stopPing();
    this.doPing();
    this.pingTimer = setInterval(() => this.doPing(), PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private async doPing(): Promise<void> {
    const id = this.sessionId;
    if (!id) return;
    try {
      await firstValueFrom(this.http.put(`${this.apiURL}/${id}/ping`, {}));
    } catch { /* ignora falha de ping */ }
  }

  /** Chama no ngOnInit do dashboard para retomar ping de sessão existente */
  resumePingIfActive(): void {
    if (this.sessionId && !this.pingTimer) this.startPing();
  }

  ngOnDestroy(): void {
    this.stopPing();
  }
}
